// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Accordion, { AccordionPresentationProps } from "@insite/mobius/Accordion";
import { AccordionSectionPresentationProps, ManagedAccordionSection } from "@insite/mobius/AccordionSection";
import { XNGAGE_CUSTOM_WIDGET } from "@sc/XDL/constants";
import { xParserOptions } from "@sc/XDL/Utilities";
import * as cssLinter from "css";
import parse from "html-react-parser";
import React, { FC } from "react";
import { css } from "styled-components";

const enum fields {
    title = "title",
    content = "content",
    expandByDefault = "expandByDefault",
    customCSS = "customCSS",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.content]: string;
        [fields.expandByDefault]: boolean;
        [fields.customCSS]: string;
    };
}

type Props = OwnProps;

interface AccordionBlockStyles {
    accordion?: AccordionPresentationProps;
    managedAccordionSection?: AccordionSectionPresentationProps;
}

export const accordionBlockStyles: AccordionBlockStyles = {};

const styles = accordionBlockStyles;

const AccordionBlock: FC<Props> = ({ fields: { title, content, customCSS, expandByDefault } }) => {
    const customCssWrapper = {
        css: css`
            ${customCSS}
        `,
    };
    return (
        <StyledWrapper {...customCssWrapper}>
            <Accordion headingLevel={4} {...styles.accordion}>
                <ManagedAccordionSection
                    initialExpanded={expandByDefault}
                    title={parse(title, xParserOptions)}
                    {...styles.managedAccordionSection}
                >
                    <StyledWrapper className="accordion-content">{parse(content, xParserOptions)}</StyledWrapper>
                </ManagedAccordionSection>
            </Accordion>
        </StyledWrapper>
    );
};

const defaultCustomCss = `.accordion-title {
}

.accordion-content {
}


`;

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const widgetModule: WidgetModule = {
    component: AccordionBlock,
    definition: {
        displayName: "Accordion Block",
        group: XNGAGE_CUSTOM_WIDGET,
        fieldDefinitions: [
            {
                name: fields.expandByDefault,
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                tab: basicTab,
            },
            {
                name: fields.title,
                displayName: "Accordion Title",
                editorTemplate: "RichTextField",
                defaultValue: "",
                fieldType: "General",
                isRequired: true,
                tab: basicTab,
            },
            {
                name: fields.content,
                displayName: "Accordion Content",
                editorTemplate: "RichTextField",
                defaultValue: "",
                fieldType: "General",
                isRequired: true,
                tab: basicTab,
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "Contextual",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};

export default widgetModule;
